<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Statement of Changes in Equity
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2 pl-2">
                  <v-col md="3" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="12" cols="12">
                      <div class="d-flex justify-start align-center flex-wrap">
                        <v-btn class="bg-primary mb-4" type="submit" @click="loadReport" elevation="2" >
                          <v-icon left>mdi-magnify</v-icon> Search
                        </v-btn>
                      </div>
                    </v-col>
                 
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="reportData" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!reportData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!reportData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Loading State -->
                <v-card v-if="loading" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <p class="mt-4">Generating report...</p>
                  </v-card-text>
                </v-card>

                <!-- Error State -->
                <v-alert v-if="error" type="error" class="mt-2">
                  {{ error }}
                </v-alert>

                <!-- Changes in Equity Report -->
                <v-card v-if="reportData && !loading" class="mt-2">
                  <v-card-title class="text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">STATEMENT OF CHANGES IN EQUITY</div>
                      <div class="text-subtitle-1">FOR THE PERIOD FROM {{ formattedDate(filters.from_date) }} TO {{ formattedDate(filters.to_date) }}</div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <div class="equity-report">
                      <!-- Header -->
                      <div class="report-header">
                        <div class="header-particulars">Particulars</div>
                        <div class="header-amounts">Amount in Taka</div>
                      </div>
                      <div class="report-subheader">
                        <div class="header-particulars"></div>
                        <div class="header-amount-box">Opening Balance</div>
                        <div class="header-amount-box">Movement</div>
                        <div class="header-amount-box">Closing Balance</div>
                      </div>

                      <!-- Equity Items -->
                      <template v-if="reportData.changes_equities">
                        <div v-for="(item, index) in reportData.changes_equities" :key="'equity-' + index" class="report-row">
                          <div class="particulars-text">{{ item.name }}</div>
                          <div class="amount-box">{{ item.opening_balance }}</div>
                          <div class="amount-box">{{ item.movement_amount }}</div>
                          <div class="amount-box">{{ item.closing_balance }}</div>
                        </div>
                      </template>

                      <!-- Total -->
                      <div class="report-row total-row">
                        <div class="particulars-text total-text">Total Changes in Equity</div>
                        <div class="amount-box total-box">{{ reportData.changes_equities_total?.opening_balance }}</div>
                        <div class="amount-box total-box">{{ reportData.changes_equities_total?.movement_amount }}</div>
                        <div class="amount-box total-box">{{ reportData.changes_equities_total?.closing_balance }}</div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- No Data State -->
                <v-alert v-if="!reportData && !loading && !error" type="info" class="mt-2">
                  Please select a date range and click "Generate Report" to view the Changes in Equity statement.
                </v-alert>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from 'vuex';

const store = useStore();
import { toast } from "vue3-toastify";
import formattedDate from '@/plugins/dateFormatter';

export default {
  name: 'ChangesInEquity',

  data: () => ({
    panel: [0],
    loading: false,
    error: null,
    reportData: null,
    filters: {
      from_date: null,
      to_date: null,
    },
  }),

  methods: {
    formattedDate,
    async loadReport() {
      if (!this.filters.from_date || !this.filters.to_date) {
        toast.error('Please select both From Date and To Date');
        return;
      }

      try {
        this.loading = true;
        this.error = null;

        const params = {
          from_date: this.formattedDate(this.filters.from_date),
          to_date: this.formattedDate(this.filters.to_date),
        };

        const response = await this.$store.dispatch('getChangesInEquity', params);
        
        if (response.success) {
          this.reportData = response.data;
          toast.success('Report generated successfully');
        } else {
          this.error = response.message || 'Failed to generate report';
          toast.error(this.error);
        }
      } catch (error) {
        console.error('Error loading Changes in Equity:', error);
        this.error = error.response?.data?.message || 'An error occurred while generating the report';
        toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.filters.from_date = null;
      this.filters.to_date = null;
      this.reportData = null;
      this.error = null;
    },

    exportToPDF() {
      if (!this.reportData) return;

      const columns = ['Particulars', 'Opening Balance', 'Movement', 'Closing Balance'];
      const rows = [];

      // Add equity items
      if (this.reportData.changes_equities) {
        this.reportData.changes_equities.forEach(item => {
          rows.push([
            item.name,
            item.opening_balance,
            item.movement_amount,
            item.closing_balance
          ]);
        });
      }

      // Add total row
      rows.push([
        'Total Changes in Equity',
        this.reportData.changes_equities_total?.opening_balance || '',
        this.reportData.changes_equities_total?.movement_amount || '',
        this.reportData.changes_equities_total?.closing_balance || ''
      ]);

      this.$exportTableToPDF(
        columns,
        rows,
        'Statement of Changes in Equity',
        'changes_in_equity_' + this.$fileDate(),
        'landscape'
      );
    },

    exportToExcel() {
      if (!this.reportData) return;

      const data = [
        ['Statement of Changes in Equity'],
        [`Period: ${this.formattedDate(this.filters.from_date)} to ${this.formattedDate(this.filters.to_date)}`],
        [],
        ['Particulars', 'Opening Balance', 'Movement', 'Closing Balance']
      ];

      // Add equity items
      if (this.reportData.changes_equities) {
        this.reportData.changes_equities.forEach(item => {
          data.push([
            item.name,
            item.opening_balance,
            item.movement_amount,
            item.closing_balance
          ]);
        });
      }

      // Add total row
      data.push([
        'Total Changes in Equity',
        this.reportData.changes_equities_total?.opening_balance || '',
        this.reportData.changes_equities_total?.movement_amount || '',
        this.reportData.changes_equities_total?.closing_balance || ''
      ]);

      this.$exportTableToExcel(data, 'changes_in_equity_' + this.$fileDate());
    },
  },
};
</script>

<style scoped>
.equity-report {
  border: 1px solid #000;
}

.report-header {
  display: grid;
  grid-template-columns: 2fr 3fr;
  background-color: #f5f5f5;
  border-bottom: 2px solid #000;
  font-weight: bold;
  font-size: 14px;
}

.report-subheader {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  background-color: #fafafa;
  border-bottom: 1px solid #000;
  font-weight: 600;
  font-size: 13px;
}

.header-particulars {
  padding: 12px;
  border-right: 1px solid #000;
  text-align: left;
}

.header-amounts {
  padding: 12px;
  text-align: center;
}

.header-amount-box {
  padding: 10px;
  border-right: 1px solid #000;
  text-align: center;
}

.header-amount-box:last-child {
  border-right: none;
}

.report-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  border-bottom: 1px solid #000;
  min-height: 40px;
  align-items: center;
}

.report-row:hover {
  background-color: #f9f9f9;
}

.particulars-text {
  padding: 10px 12px;
  border-right: 1px solid #000;
  text-align: left;
  font-size: 13px;
}

.amount-box {
  padding: 10px 12px;
  border-right: 1px solid #000;
  text-align: right;
  font-size: 13px;
}

.amount-box:last-child {
  border-right: none;
}

.total-row {
  border-top: 2px solid #000;
  border-bottom: 2px solid #000;
  font-weight: bold;
}

.total-text {
  font-weight: bold;
}

.total-box {
  font-weight: bold;
}

@media print {
  .equity-report {
    border: 1px solid #000;
  }
  
  .report-row:hover {
    background-color: transparent;
  }
}
</style>

